import { useState, useMemo, useContext } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  TrendingUp, DollarSign, Sparkles, Target, AlertTriangle, Loader2, RefreshCw, Grid3X3,
  Info
} from 'lucide-react';
import FormulaReveal, { HOTEL_FORMULAS } from '@/components/ui/FormulaReveal';
import { useAIInsight } from '@/hooks/useAIInsight';
import { DashboardContext } from '@/contexts/DashboardContext';
import { formatCurrency } from '@/helpers/formatters';
import { useCompset } from '@/contexts/CompsetContext';
import { usePlans } from '@/contexts/PlanRulesContext';
import FeatureRestrict from './FeatureRestrict';
import DashboardError from '../error/DashboardError';
import AmenityHeatmap from '../charts/AmenityHeatmap';

export default function AdvancedAnalytics() {

  const { advancedAnalytics,
    isLoadingAdvancedAnalytics,
    isErrorAdvancedAnalytics,
    isEnabledAdvancedAnalytics,
    allAiInsights
  } = useContext(DashboardContext);

  const [activeTab, setActiveTab] = useState('overview');
  const [chartTimeRange, setChartTimeRange] = useState('7d');
  const { canAccessAiInsights, calculateAiInsightRefreshCount, canAccessAdvanceAnalysis, maxAiRefreshPerSection } = usePlans()

  const { activeCompset } = useCompset()


  const { best_ota, worst_ota } = useMemo(() => {

    if(!advancedAnalytics?.revenue_leakage?.length) return { best_ota: "", worst_ota: "" };

    const sortedByLoss = [...advancedAnalytics.revenue_leakage].sort((a, b) => (b.loss || 0) - (a.loss || 0));

    return {
      worst_ota: sortedByLoss?.[0]?.ota || "",
      best_ota: sortedByLoss?.slice(-1)?.[0]?.ota || ""
    }
  }, [advancedAnalytics])

  // AI Insight for advanced analytics - must be before any conditional returns
  const aiAdvancedData = useMemo(() => ({
    revenue_at_risk: advancedAnalytics?.summary?.revenue_at_risk || 0,
    compliance_rate: advancedAnalytics?.summary?.avg_compliance || 0,
    // best_ota: advancedAnalytics?.ota_performance?.[0]?.ota,
    best_ota: best_ota,
    worst_ota: worst_ota
  }), [advancedAnalytics]);


  const { insight: aiAdvancedInsight, loading: aiAdvancedLoading, refetch: refetchAdvancedAI, isAIGenerated: isAdvancedAIGenerated, isEnabled, enableAiInsight } = useAIInsight(
    'advanced',
    aiAdvancedData,
    (!isLoadingAdvancedAnalytics && isEnabledAdvancedAnalytics && !!advancedAnalytics && canAccessAiInsights())
  );

  if (isLoadingAdvancedAnalytics) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading advanced analytics...</p>
        </div>
      </div>
    );
  }

  const hasAiInsightData = allAiInsights?.insights?.find(insight => insight?.section === "advanced");

  if (isErrorAdvancedAnalytics) {
    return <DashboardError />
  }

  if (!canAccessAdvanceAnalysis()) {
    return <FeatureRestrict />
  }

  const summary = advancedAnalytics?.summary || {};
  const otaPerformance = advancedAnalytics?.ota_performance || [];
  const marketShare = advancedAnalytics?.market_share || [];
  const totalPotentialLoss = summary?.revenue_at_risk;
  const totalViolations = summary?.total_violations;
  const avgComplianceRate = summary?.average_compliance;
  const activeOtas = summary?.active_otas;

  return (
    <div className="space-y-6 animate-in fade-in duration-500" data-testid="advanced-analytics">
      {/* Summary Cards with Modern Gradients */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="relative overflow-hidden border-2 border-red-500/10 hover:border-red-500/30 transition-all hover:shadow-xl group">
          <div className="absolute inset-0 bg-gradient-to-br from-red-500/10 to-pink-500/10 opacity-50" />
          <CardContent className="relative p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-gradient-to-br from-red-500 to-pink-500 shadow-lg group-hover:scale-110 transition-transform">
                  <DollarSign className="h-5 w-5 text-white" />
                </div>
                <div className=''>
                  <p className="text-sm text-muted-foreground">Revenue at Risk</p>
                  <p className="text-2xl font-bold text-foreground">{formatCurrency(totalPotentialLoss, activeCompset?.currency)}</p>
                </div>
              </div>
              <FormulaReveal
                {...HOTEL_FORMULAS.REVENUE_AT_RISK}
                variables={{
                  'total_loss': { value: `${formatCurrency(totalPotentialLoss, activeCompset?.currency)}`, description: 'Calculated potential loss' }
                }}
              >
                <Button variant="ghost" size="icon" className="h-7 w-7">
                  <Info className="h-3.5 w-3.5 text-muted-foreground hover:text-primary" />
                </Button>
              </FormulaReveal>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-amber-500/10">
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Undercuts</p>
                  <p className="text-2xl font-bold text-foreground">{totalViolations}</p>
                </div>
              </div>
              <FormulaReveal
                title="Total undercuts"
                type="kpi"
                formula="Total_Undercuts = SUM(undercuts) for all OTAs"
                description="Aggregate count of rate parity undercuts across all channels."
                dataSource="Rate Monitoring System"
              >
                <Button variant="ghost" size="icon" className="h-7 w-7">
                  <Info className="h-3.5 w-3.5 text-muted-foreground hover:text-primary" />
                </Button>
              </FormulaReveal>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-emerald-500/10">
                  <Target className="h-5 w-5 text-emerald-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Avg Compliance</p>
                  <p className="text-2xl font-bold text-foreground">{avgComplianceRate ?? 0}%</p>
                </div>
              </div>
              <FormulaReveal
                {...HOTEL_FORMULAS.OTA_COMPLIANCE_RATE}
                variables={{
                  'compliance_rate': { value: `${avgComplianceRate}%`, description: 'Average across OTAs' }
                }}
              >
                <Button variant="ghost" size="icon" className="h-7 w-7">
                  <Info className="h-3.5 w-3.5 text-muted-foreground hover:text-primary" />
                </Button>
              </FormulaReveal>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-primary/10">
                  <TrendingUp className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Active OTAs</p>
                  <p className="text-2xl font-bold text-foreground">{activeOtas}</p>
                </div>
              </div>
              <FormulaReveal
                title="Active OTA Channels"
                type="kpi"
                formula="Active_OTAs = COUNT(DISTINCT OTA_names WHERE has_rate_data = true)"
                description="Number of OTA platforms actively listing your hotel."
                dataSource="Channel Manager"
              >
                <Button variant="ghost" size="icon" className="h-7 w-7">
                  <Info className="h-3.5 w-3.5 text-muted-foreground hover:text-primary" />
                </Button>
              </FormulaReveal>
            </div>
          </CardContent>
        </Card>
      </div>


      {/* Tabs for Different Analytics Views */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-primary/5">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          {/* <TabsTrigger value="revenue">Revenue Leakage</TabsTrigger> */}
          <TabsTrigger value="market">Market Share</TabsTrigger>
        </TabsList>

        {/* <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">OTA Performance</CardTitle>
                    <CardDescription>Compliance and pricing by channel</CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <FormulaReveal
                      title="OTA Performance Table"
                      type="data"
                      formula="Compliance_Rate = (Days_At_Parity / Total_Days) × 100"
                      description="Shows performance metrics for each OTA distribution channel."
                      dataSource="Channel Manager & Rate Monitoring"
                    >
                      <Button variant="ghost" size="icon" className="h-7 w-7">
                        <Eye className="h-3.5 w-3.5 text-muted-foreground hover:text-primary" />
                      </Button>
                    </FormulaReveal>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {otaPerformance?.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>OTA</TableHead>
                        <TableHead className="text-right">Avg Price</TableHead>
                        <TableHead className="text-center">Undercuts</TableHead>
                        <TableHead className="text-center">Compliance</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {otaPerformance?.map((ota, index) => (
                        <TableRow key={index}>
                          <TableCell className="font-medium">{ota?.name || ota?.ota}</TableCell>
                          <TableCell className="text-right">{formatCurrency(ota?.avg_price, activeCompset?.currency)}</TableCell>
                          <TableCell className="text-center">
                            {ota?.violations > 0 ? (
                              <Badge variant="destructive">{ota?.violations}</Badge>
                            ) : (
                              <Badge variant="outline"> {ota?.violations} </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex items-center gap-2 justify-center">
                              <Progress value={ota?.compliance_rate} className="w-16 h-2" />
                              <span className={`text-sm ${(ota?.compliance_rate) >= 80 ? 'text-emerald-500' : 'text-amber-500'}`}>
                                {(ota?.compliance_rate).toFixed(0)}%
                              </span>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    No OTA performance data
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">OTA Price Comparison</CardTitle>
                  <CardDescription>Average rates by distribution channel</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <FormulaReveal
                    title="OTA Price Comparison Chart"
                    type="chart"
                    formula="Avg_OTA_Rate = SUM(Daily_Rates) / Days_Monitored"
                    description="Compares average pricing across all OTA distribution channels."
                    dataSource="Rate Scraping Engine"
                  >
                    <Button variant="ghost" size="icon" className="h-7 w-7">
                      <Eye className="h-3.5 w-3.5 text-muted-foreground hover:text-primary" />
                    </Button>
                  </FormulaReveal>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {otaPerformance?.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={otaPerformance} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis
                      type="number"
                      tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                      tickFormatter={(v) => `${formatCurrency(v, activeCompset?.currency)}`} />
                    <YAxis
                      type="category"
                      label={{ value: "OTA's", angle: -90 }}
                      dataKey="ota" tick={false} />
                    <Tooltip content={OTATooltip} />
                    <Bar dataKey="avg_price" fill="hsl(var(--primary))" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                  No OTA data available
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent> */}

        {/* Tab 2: Revenue Leakage Opportunities */}
        <TabsContent value="overview" className="space-y-6">
          <Card className="border-2 border-primary/10">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-amber-500" />
                    Revenue Leakage Opportunities (Today)
                  </CardTitle>
                  <CardDescription>Direct vs OTA pricing undercuts causing revenue loss</CardDescription>
                </div>
                <FormulaReveal
                  title="Revenue Leakage Table"
                  type="data"
                  formula="Daily_Loss = (Direct_Rate - OTA_Rate) × Estimated_Bookings_Lost"
                  description="Calculates potential revenue loss from OTA rate undercutting for today."
                  dataSource="Rate Monitoring & Booking Analytics"
                >
                  <Button variant="ghost" size="icon" className="h-7 w-7">
                    <Info className="h-3.5 w-3.5 text-muted-foreground hover:text-primary" />
                  </Button>
                </FormulaReveal>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>OTA Platform</TableHead>
                    <TableHead className="text-right">Direct Rate</TableHead>
                    <TableHead className="text-right">OTA Rate</TableHead>
                    <TableHead className="text-right">Difference</TableHead>
                    <TableHead className="text-center">Est. Loss/Day/Room</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {advancedAnalytics?.revenue_leakage?.length > 0

                    ? advancedAnalytics?.revenue_leakage?.map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{row?.ota}</TableCell>
                        <TableCell className="text-right">{formatCurrency(row?.direct, activeCompset?.currency)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(row?.otaRate, activeCompset?.currency)}</TableCell>
                        <TableCell className="text-right">
                          <span className={row?.diff > 0 ? 'text-red-500 font-medium' : 'text-emerald-500'}>
                            {row?.diff < 0 ? '' : ''}{row?.diff}%
                          </span>
                        </TableCell>
                        <TableCell className="text-center font-medium">
                          {row?.loss
                            ? `${formatCurrency(Number(row?.loss), activeCompset?.currency)}`
                            : '-'
                          }
                        </TableCell>
                        <TableCell>
                          <Badge variant={
                            row?.status === 'High' ? 'destructive' :
                              row?.status === 'Medium' ? 'default' :
                                row?.status === 'Low' ? 'secondary' : 'outline'
                          }>
                            {row?.status}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))
                    : <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">Revenue leakage data is currently unavailable</TableCell>
                    </TableRow>
                  }
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="market" className="space-y-6">
          {/* Amenity Competitive Positioning Chart */}
          {/* <Card className="border-2 border-primary/10">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-primary" />
                    Amenity Competitive Positioning
                  </CardTitle>
                  <CardDescription>Your hotel's amenity sentiment vs market average</CardDescription>
                </div>
                <FormulaReveal
                  title="Amenity Competitive Positioning"
                  type="chart"
                  formula="Gap = Your_Positive_% - AVG(Competitor_Positive_%)"
                  description="Compares your hotel's positive sentiment percentage for each amenity against the market average of all competitors. Positive gaps indicate competitive advantages, negative gaps highlight improvement opportunities."
                  dataSource="Guest Review Sentiment Analysis"
                  variables={{
                    'Your_Positive_%': { value: 'Your amenity score', description: 'Percentage of positive reviews mentioning this amenity for your hotel' },
                    'Competitor_Positive_%': { value: 'Competitor scores', description: 'Percentage of positive reviews for each competitor' },
                    'Gap': { value: 'Difference', description: 'Your score minus market average (positive = outperforming)' }
                  }}
                >
                  <Button variant="ghost" size="icon" className="h-7 w-7">
                    <Info className="h-3.5 w-3.5 text-muted-foreground hover:text-primary" />
                  </Button>
                </FormulaReveal>
              </div>
            </CardHeader>
            <CardContent>
              <AmenityCompetitiveChart data={advancedAnalytics?.market_share} height={420} />
            </CardContent>
          </Card> */}

          {/* Amenity Heatmap - Dense Comparison Matrix */}
          <Card className="border-2 border-primary/10">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Grid3X3 className="h-5 w-5 text-primary" />
                    Amenity Sentiment Heatmap
                  </CardTitle>
                  <CardDescription>Dense comparison matrix across all hotels in your compset</CardDescription>
                </div>
                <FormulaReveal
                  title="Amenity Sentiment Heatmap"
                  type="chart"
                  formula="Cell_Value = (Positive_Reviews / Total_Reviews) × 100"
                  description="A dense matrix showing positive sentiment percentages for each amenity across all hotels in your competitive set. Colors range from red (low sentiment) to green (high sentiment), enabling quick identification of strengths and weaknesses across the market."
                  dataSource="Guest Review Sentiment Analysis"
                  variables={{
                    'Positive_Reviews': { value: 'Count', description: 'Number of reviews with positive sentiment for this amenity' },
                    'Total_Reviews': { value: 'Count', description: 'Total reviews mentioning this amenity' },
                    'Color_Scale': { value: 'Red → Green', description: 'Red (0-50%), Orange (50-70%), Yellow (70-80%), Green (80-100%)' }
                  }}
                >
                  <Button variant="ghost" size="icon" className="h-7 w-7">
                    <Info className="h-3.5 w-3.5 text-muted-foreground hover:text-primary" />
                  </Button>
                </FormulaReveal>
              </div>
            </CardHeader>
            <CardContent>
              <AmenityHeatmap data={advancedAnalytics?.market_share} maxAmenities={20} />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab 2: Market Share Analysis _pie chart removed code  */}
        {/* <TabsContent value="market" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-2 border-primary/10">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Target className="h-5 w-5 text-primary" />
                      Review Market Share
                    </CardTitle>
                    <CardDescription>Distribution of reviews across competitive set</CardDescription>
                  </div>
                  <FormulaReveal
                    title="Review Market Share Chart"
                    type="chart"
                    formula="Share = (Your_Reviews / Total_Compset_Reviews) × 100"
                    description="Shows your hotel's market share based on review volume."
                    dataSource="Review Aggregation"
                  >
                    <Button variant="ghost" size="icon" className="h-7 w-7">
                      <Eye className="h-3.5 w-3.5 text-muted-foreground hover:text-primary" />
                    </Button>
                  </FormulaReveal>
                </div>
              </CardHeader>
              <CardContent>
                {marketShare?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={marketShare}
                        dataKey="reviews"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                      // label={(entry) => `${entry?.name}: ${entry?.share}%`}
                      >
                        {marketShare.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip content={MarketShareTooltip} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                    No market share data available
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-2 border-primary/10">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">Market Share Breakdown</CardTitle>
                    <CardDescription>Detailed distribution analysis</CardDescription>
                  </div>
                  <FormulaReveal
                    title="Market Share Breakdown"
                    type="data"
                    formula="Share_% = (Hotel_Reviews / Total_Reviews) × 100"
                    description="Detailed breakdown of market share distribution."
                    dataSource="Review Aggregation"
                  >
                    <Button variant="ghost" size="icon" className="h-7 w-7">
                      <Eye className="h-3.5 w-3.5 text-muted-foreground hover:text-primary" />
                    </Button>
                  </FormulaReveal>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {marketShare.length > 0 ? marketShare.map((hotel, index) => (
                    <div key={index} className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className={`font-medium ${index === 0 ? 'text-primary' : 'text-foreground'}`}>
                          {hotel.name?.split(',')[0]}
                          {index === 0 && <Badge variant="default" className="ml-2 text-xs">You</Badge>}
                        </span>
                        <span className="text-muted-foreground">{hotel.share}%</span>
                      </div>
                      <Progress value={hotel.share} className="h-2" />
                    </div>
                  )) : (
                    <div className="text-center py-8 text-muted-foreground">
                      No data available
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent> */}

        {/* Tab 4: Demand Calendar */}
        {/* <TabsContent value="demand" className="space-y-6">
          <DemandCalendar />
        </TabsContent> */}
      </Tabs>
      {/* AI Insight */}
      {
        canAccessAiInsights() && (
          <Card className="bg-gradient-to-r from-primary/5 via-accent/5 to-primary/5 border-primary/20" data-testid="ai-advanced-card">
            <CardContent className="p-4 sm:p-6">

              {/* ── Header ──────────────────────────────────────────────────────────────
        Mobile:  2 rows
          Row 1: [Icon + Title + Badge]
          Row 2:             justify-end → [Refresh/Generate] [x/x left] [ℹ]
        Desktop: 1 row
          [Icon + Title + Badge] ─── flex-1 ─── [Refresh/Generate] [x/x left] [ℹ]
    ─────────────────────────────────────────────────────────────────────────── */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">

                {/* Left: Icon + Title + Badge */}
                <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                  <div className="p-2.5 sm:p-3 rounded-xl bg-accent/10 shrink-0">
                    {aiAdvancedLoading ? (
                      <Loader2 className="h-5 w-5 sm:h-6 sm:w-6 text-accent animate-spin" />
                    ) : (
                      <Sparkles className="h-5 w-5 sm:h-6 sm:w-6 text-accent" />
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap min-w-0">
                    <h3 className="font-semibold text-foreground text-base sm:text-lg leading-tight">AI Revenue Optimization Insight</h3>
                    {isAdvancedAIGenerated && (
                      <Badge variant="outline" className="text-xs bg-purple-500/10 text-purple-600 border-purple-500/30">
                        Prowen AI
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Right: Actions — justify-end on mobile, inline on desktop */}
                <div className="flex items-center justify-end gap-2 shrink-0">
                  {(calculateAiInsightRefreshCount("advanced") && (isEnabled || hasAiInsightData))
                    ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="gap-1.5 h-8 px-2 sm:px-3 shrink-0"
                        onClick={() => {
                          enableAiInsight()
                          refetchAdvancedAI("regenerate")
                        }}
                        disabled={aiAdvancedLoading}
                        title="Refresh AI Analysis"
                      >
                        <RefreshCw className={`h-4 w-4 shrink-0 ${aiAdvancedLoading ? 'animate-spin' : ''}`} />
                        <span className="text-xs sm:hidden">Refresh</span>
                      </Button>
                    )
                    : null}
                  {(!isEnabled && !hasAiInsightData) && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2 h-8 shrink-0"
                      onClick={enableAiInsight}
                    >
                      <span className="text-xs">Generate AI Insights</span>
                    </Button>
                  )}
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {`${calculateAiInsightRefreshCount("advanced")} / ${maxAiRefreshPerSection} left`}
                  </span>
                  <FormulaReveal {...HOTEL_FORMULAS.ADVANCE_ANALYTICS_AI}>
                    <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0">
                      <Info className="h-4 w-4 text-muted-foreground hover:text-primary" />
                    </Button>
                  </FormulaReveal>
                </div>
              </div>

              {/* Body */}
              <p className="text-sm font-medium leading-relaxed whitespace-pre-wrap break-words">
                {aiAdvancedLoading
                  ? 'Analyzing revenue optimization with Prowen AI...'
                  : aiAdvancedInsight
                    ? aiAdvancedInsight
                    : hasAiInsightData
                      ? hasAiInsightData?.insight
                      : null
                }
              </p>

            </CardContent>
          </Card>
        )
      }
    </div>
  );
}
